/**
 * RejectOrderDialog - Dialog for rejecting an order
 */
import React, { useState } from 'react';
import { useOrderStore } from '../../stores/orderStore';
import type { Order } from '../../types';
import '../Dialogs/Dialog.css';

interface RejectOrderDialogProps {
  isOpen: boolean;
  order: Order | null;
  onClose: () => void;
  onSuccess: () => void;
}

export const RejectOrderDialog: React.FC<RejectOrderDialogProps> = ({ 
  isOpen, 
  order, 
  onClose,
  onSuccess 
}) => {
  const { rejectOrder } = useOrderStore();
  const [reason, setReason] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!isOpen || !order) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!reason.trim()) {
      setError('Musíte uviesť dôvod zamietnutia');
      return;
    }

    setError(null);
    setIsSubmitting(true);

    try {
      await rejectOrder(order.id, reason);
      handleClose();
      onSuccess();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Chyba pri zamietaní objednávky');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setReason('');
    setError(null);
    onClose();
  };

  const customerName = order.customer_type === 'individual' 
    ? `${order.first_name} ${order.last_name}`
    : order.company_name;

  return (
    <div className="dialog-overlay" onClick={handleClose}>
      <div className="dialog-container" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px' }}>
        <div className="dialog-header">
          <h2>❌ Zamietnuť objednávku</h2>
          <button className="dialog-close" onClick={handleClose}>×</button>
        </div>

        <form onSubmit={handleSubmit} className="dialog-content">
          {error && (
            <div className="error-message" style={{ 
              marginBottom: '1rem', 
              padding: '0.75rem', 
              backgroundColor: '#fee', 
              border: '1px solid #fcc', 
              borderRadius: '4px', 
              color: '#c00' 
            }}>
              {error}
            </div>
          )}

          {/* Order Info */}
          <div style={{ 
            padding: '1rem', 
            backgroundColor: '#f5f5f5', 
            borderRadius: '8px', 
            marginBottom: '1.5rem' 
          }}>
            <h3 style={{ margin: '0 0 0.5rem 0', fontSize: '1.1rem' }}>
              {customerName}
            </h3>
            <div style={{ fontSize: '0.9rem', color: '#666' }}>
              <div><strong>Číslo objednávky:</strong> {order.order_number}</div>
              <div><strong>Adresa:</strong> {order.address}, {order.city}</div>
              <div><strong>Email:</strong> {order.email}</div>
              <div><strong>Telefón:</strong> {order.phone}</div>
            </div>
          </div>

          {/* Rejection Reason */}
          <div className="form-group">
            <label style={{ fontWeight: 'bold', fontSize: '1rem' }}>
              Dôvod zamietnutia *
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={4}
              placeholder="Uveďte dôvod zamietnutia objednávky..."
              required
              style={{ 
                width: '100%', 
                padding: '0.75rem',
                border: '2px solid #ddd',
                borderRadius: '4px',
                fontSize: '0.95rem'
              }}
            />
            <div style={{ fontSize: '0.85rem', color: '#666', marginTop: '0.5rem' }}>
              Tento dôvod bude uložený v interných poznámkach
            </div>
          </div>

          <div className="dialog-actions">
            <button type="button" onClick={handleClose} disabled={isSubmitting}>
              Zrušiť
            </button>
            <button 
              type="submit" 
              className="btn-primary" 
              disabled={isSubmitting || !reason.trim()}
              style={{ backgroundColor: '#dc3545' }}
            >
              {isSubmitting ? 'Zamietam...' : '❌ Zamietnuť objednávku'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

